/** Round over and match over: scores, running totals, ready-up, play again. scope.md §8.1. */

import { useState } from 'react';
import type { Card, PlayerView, ReactionId } from '@garbage/shared';
import { ROUNDS, plural } from '@garbage/shared';
import { contractFor, exampleFor } from '@garbage/engine';
import { CardFace } from './Card.js';
import { useContractText, useT } from '../i18n.js';
import { SettingsButton } from './SettingsSheet.js';
import { loadWins } from '../wins.js';
import { ReactionBubble, ReactionPicker, useLiveReactions } from './Reactions.js';

export function RoundOver({
  view, ready, onNextRound, isHost = false, host = null, onPlayAgain, onLeave, onReact, onSettings, onFeedback,
}: {
  view: PlayerView;
  ready: string[];
  onNextRound: () => void;
  isHost?: boolean;
  /** Who can start the next game, so everyone else knows who they are waiting for. */
  host?: string | null;
  /** Host only, once the match is over: back to the lobby with the same people. */
  onPlayAgain?: () => void;
  onLeave?: () => void;
  onReact?: (reaction: ReactionId) => void;
  onSettings?: () => void;
  onFeedback?: () => void;
}) {
  const t = useT();
  const contractText = useContractText();
  const liveReactions = useLiveReactions();
  const [asked, setAsked] = useState(false);

  const matchOver = view.status === 'match-over';
  const winnerId = matchOver ? view.matchWinner : view.roundWinner;
  const winner = view.players.find((p) => p.id === winnerId);
  const youWon = winnerId === view.you;
  const hostName = view.players.find((p) => p.id === host)?.name ?? null;

  // Lowest total wins, so the table is sorted that way.
  const standings = [...view.players].sort((a, b) => a.score - b.score);

  // What is coming next, shown before anybody commits to being dealt in.
  const nextRound = view.round + 1;
  const nextContract = matchOver ? null : contractFor(nextRound, view.rules);
  const nextExample = nextContract ? exampleFor(nextContract, 6 + nextRound + 1) : [];

  return (
    // Scrolls when it has to, and stays centred when it does not.
    <main className="relative h-full overflow-y-auto px-5 pt-[calc(1rem+var(--pad-top))] pb-[calc(1rem+var(--pad-bottom))] text-center">
      <div className="flex min-h-full flex-col items-center justify-center gap-5">
        {onSettings && (
          <div className="absolute right-4 top-[calc(1rem+var(--pad-top))]">
            <SettingsButton onClick={onSettings} />
          </div>
        )}
        <p className="text-5xl" aria-hidden="true">{youWon ? '🎉' : '👏'}</p>
        <h1 className="text-2xl font-black text-sun" data-testid="result-heading">
          {matchOver
            ? t('result.matchWinner', { name: winner?.name ?? t('common.nobody') })
            : winner
              ? t('result.wentOut', { name: winner.name })
              : t('result.ranOut')}
        </h1>
        {matchOver && youWon && loadWins() > 0 && (
          <p className="-mt-3 text-sm font-bold text-bone/70" data-testid="wins-total">
            🏆 {plural(t, 'home.wins', loadWins(), { count: loadWins() })}
          </p>
        )}
        {!matchOver && (
          <p className="-mt-3 text-xs text-bone/50">{t('common.roundOf', { round: view.round, rounds: ROUNDS })}</p>
        )}

        <table className="w-full max-w-xs text-sm" data-testid="scoreboard">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-bone/40">
              <th className="pb-1 text-left font-bold">{t('score.player')}</th>
              <th className="pb-1 text-right font-bold">{t('score.thisRound')}</th>
              <th className="pb-1 text-right font-bold">{t('score.total')}</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((player) => {
              const reaction = liveReactions.get(player.id);
              return (
                <tr key={player.id} data-testid={`score-${player.id}`} className="border-t border-white/10">
                  <td className="py-1.5 text-left font-bold">
                    {player.name}
                    {view.status === 'round-over' && (
                      <span
                        data-testid={`ready-${player.id}`}
                        aria-label={ready.includes(player.id) ? t('score.ready') : t('score.notReady')}
                        className={ready.includes(player.id) ? 'ml-1 text-sun' : 'ml-1 text-bone/25'}
                      >
                        ✓
                      </span>
                    )}
                    {reaction && <ReactionBubble reaction={reaction} className="ml-1 align-middle" />}
                  </td>
                  <td className="py-1.5 text-right text-bone/60">
                    {player.roundScore === null ? '—' : player.roundScore}
                  </td>
                  <td className="py-1.5 text-right font-black">{player.score}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="-mt-3 text-[10px] text-bone/40">{t('score.lowestWins')}</p>

        {onReact && <ReactionPicker onReact={onReact} />}

        {nextContract && (
          <section className="w-full max-w-sm" data-testid="next-round-preview">
            <p className="mb-1 text-[11px] font-bold uppercase tracking-widest text-bone/40">
              {t('next.header', { round: nextRound, rounds: ROUNDS, cards: 6 + nextRound })}
            </p>
            <p className="mb-2 text-lg font-black text-sun" data-testid="next-contract">
              {contractText(nextContract)}
            </p>
            <div className="flex flex-wrap justify-center gap-3 rounded-xl bg-black/25 p-2.5">
              {nextExample.map((meld, i) => (
                <div key={i} className="flex gap-0.5">
                  {meld.cards.map((c, n) => (
                    <CardFace key={n} card={exampleCard(c, `next-${i}-${n}`)} size="tiny" />
                  ))}
                </div>
              ))}
            </div>
          </section>
        )}

        {!matchOver && !ready.includes(view.you) && (
          <button
            type="button"
            data-testid="next-round"
            onClick={onNextRound}
            className="rounded-2xl bg-sun px-8 py-4 text-xl font-black text-ink active:scale-95"
          >
            {t('roundOver.dealMeIn')}
          </button>
        )}
        {!matchOver && ready.includes(view.you) && (
          <p data-testid="waiting-for-others" className="text-sm text-bone/60">
            {t('common.waitingFor', { names: view.players.filter((p) => !ready.includes(p.id)).map((p) => p.name).join(', ') })}
          </p>
        )}

        {matchOver && (
          <div className="flex w-full max-w-xs flex-col gap-2">
            {isHost && onPlayAgain && (
              <button
                type="button"
                data-testid="play-again"
                disabled={asked}
                onClick={() => { setAsked(true); onPlayAgain(); }}
                className="rounded-2xl bg-sun px-8 py-4 text-xl font-black text-ink active:scale-95 disabled:opacity-50"
              >
                {t('matchOver.playAgain')}
              </button>
            )}
            {!isHost && (
              <p data-testid="waiting-for-host" className="text-sm text-bone/60">
                {t('matchOver.waitingForHost', { name: hostName ?? t('common.someone') })}
              </p>
            )}
            {onLeave && (
              <button
                type="button"
                data-testid="leave-table"
                onClick={onLeave}
                className="rounded-2xl bg-white/10 py-3 font-bold"
              >
                {t('matchOver.leave')}
              </button>
            )}
            {onFeedback && (
              <button
                type="button"
                data-testid="match-feedback"
                onClick={onFeedback}
                className="text-sm font-bold text-bone/60 underline"
              >
                ✉ {t('matchOver.feedback')}
              </button>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

/** The preview is illustration, not real cards, so it gets its own ids. */
function exampleCard(spec: { rank: Card['rank']; suit: Card['suit'] }, id: string): Card {
  return { id, rank: spec.rank, ...(spec.suit ? { suit: spec.suit } : {}) };
}
